import React, { useState } from 'react';

interface Task {
  id: string;
  number: number;
  title: string;
  description: string;
  requirements: Array<{
    text: string;
    subitems?: string[];
  }>;
  verification: string[];
}

const tasks: Task[] = [
  {
    id: 'task-1',
    number: 1,
    title: 'Fix Python Environment',
    description: 'Restore the Python virtual environment and install all required dependencies for the Django application to function properly.',
    requirements: [
      {
        text: 'Locate the Django project directory and identify the Python version requirements'
      },
      {
        text: 'Create or repair the Python virtual environment'
      },
      {
        text: 'Install Django and all required dependencies from requirements file'
      },
      {
        text: 'Verify Python packages are correctly installed and accessible'
      },
      {
        text: 'Test that Django management commands are available'
      }
    ],
    verification: [
      'Python virtual environment is active and functional',
      'Django is installed and importable',
      'All required Python packages are installed',
      'Django management commands execute without import errors',
      'Python version meets application requirements'
    ]
  },
  {
    id: 'task-2',
    number: 2,
    title: 'Fix Database Configuration',
    description: 'Repair the PostgreSQL database connection and apply all pending migrations to ensure the database schema is correct.',
    requirements: [
      {
        text: 'Verify PostgreSQL service is running and accessible'
      },
      {
        text: 'Check database credentials and connection settings in Django settings (required for grading: NAME = \"djangoapp\", USER = \"djangouser\", PASSWORD = \"django123\")'
      },
      {
        text: 'Test database connectivity from Django shell'
      },
      {
        text: 'Run database migrations to update schema:',
        subitems: ['Check for pending migrations', 'Apply all migrations safely', 'Verify migration success']
      },
      {
        text: 'Create a superuser account if none exists'
      }
    ],
    verification: [
      'PostgreSQL service is active and running',
      'Django can connect to the database successfully',
      'All database migrations are applied',
      'Database tables exist with correct schema',
      'Superuser account is available for admin access'
    ]
  },
  {
    id: 'task-3',
    number: 3,
    title: 'Configure Static Files',
    description: 'Set up static file serving correctly so CSS, JavaScript, and image assets load properly for the application.',
    requirements: [
      {
        text: 'Identify the correct STATIC_ROOT and STATIC_URL settings'
      },
      {
        text: 'Create necessary directories for static files with proper permissions'
      },
      {
        text: 'Collect all static files from Django apps using management command'
      },
      {
        text: 'Configure web server to serve static files:',
        subitems: ['Set correct directory paths', 'Configure appropriate aliases', 'Ensure proper permissions']
      },
      {
        text: 'Test that static files are accessible via browser'
      }
    ],
    verification: [
      'STATIC_ROOT directory exists with collected files',
      'Static files have correct ownership and permissions',
      'CSS stylesheets load correctly in the application',
      'JavaScript files are served without 404 errors',
      'Admin interface displays with proper styling'
    ]
  },
  {
    id: 'task-4',
    number: 4,
    title: 'Fix Application Settings',
    description: 'Correct critical Django settings including DEBUG mode, allowed hosts, secret key, and security configurations.',
    requirements: [
      {
        text: 'Set DEBUG to False for production security'
      },
      {
        text: 'Configure ALLOWED_HOSTS with appropriate domain names and IP addresses'
      },
      {
        text: 'Change SECRET_KEY from the insecure placeholder value'
      },
      {
        text: 'Configure security settings:',
        subitems: [
          'Enable CSRF protection',
          'Set secure cookie settings',
          'Configure SECURE_SSL_REDIRECT if using HTTPS',
          'Set appropriate CORS headers if needed'
        ]
      },
      {
        text: 'Verify email backend configuration for notifications'
      },
      {
        text: 'Test configuration with Django check command'
      }
    ],
    verification: [
      'DEBUG mode is disabled (False)',
      'ALLOWED_HOSTS includes the server hostname/IP',
      'SECRET_KEY no longer uses the insecure placeholder',
      'CSRF protection is enabled and functional',
      'Django check command reports no critical issues',
      'Application loads without debug information exposed'
    ]
  },
  {
    id: 'task-5',
    number: 5,
    title: 'Configure Web Server',
    description: 'Set up the web server to properly serve the Django application using WSGI and ensure it starts automatically.',
    requirements: [
      {
        text: 'Install and configure a WSGI server (Gunicorn or uWSGI)'
      },
      {
        text: 'Create a systemd service file for the Django application'
      },
      {
        text: 'Configure the web server (Nginx/Apache) to proxy requests to the WSGI server (required: Nginx listens on port 80)'
      },
      {
        text: 'Set up proper logging for application errors and access logs'
      },
      {
        text: 'Enable the service to start automatically on boot'
      },
      {
        text: 'Test the complete request flow from web server to Django application'
      }
    ],
    verification: [
      'WSGI server is installed and configured',
      'Django application service is active and running',
      'Web server successfully proxies requests to Django',
      'Application responds to HTTP requests correctly',
      'Service is enabled for automatic startup',
      'Logs are being generated and accessible'
    ]
  }
];

const DjangoApplicationDisasterInstructions: React.FC = () => {
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [completedChecks, setCompletedChecks] = useState<Set<string>>(new Set());
  const [completedTasks, setCompletedTasks] = useState<Set<number>>(new Set());

  const handleTaskClick = (index: number) => {
    setCurrentTaskIndex(index);
  };

  const handleNextTask = () => {
    if (currentTaskIndex < tasks.length - 1) {
      setCurrentTaskIndex(currentTaskIndex + 1);
    } else {
      setCurrentTaskIndex(0);
    }
  };

  const handlePreviousTask = () => {
    if (currentTaskIndex > 0) {
      setCurrentTaskIndex(currentTaskIndex - 1);
    }
  };

  const toggleVerification = (taskId: string, checkIndex: number) => {
    const checkId = `${taskId}-check-${checkIndex}`;
    const newChecks = new Set(completedChecks);
    
    if (newChecks.has(checkId)) {
      newChecks.delete(checkId);
    } else {
      newChecks.add(checkId);
    }
    
    setCompletedChecks(newChecks);

    // Check if all verifications for this task are completed
    const currentTask = tasks[currentTaskIndex];
    const allChecksCompleted = currentTask.verification.every(
      (_, idx) => newChecks.has(`${currentTask.id}-check-${idx}`)
    );

    if (allChecksCompleted && !completedTasks.has(currentTaskIndex)) {
      const newCompletedTasks = new Set(completedTasks);
      newCompletedTasks.add(currentTaskIndex);
      setCompletedTasks(newCompletedTasks);
    }
  };

  const currentTask = tasks[currentTaskIndex];
  const progressPercentage = ((currentTaskIndex + 1) / tasks.length) * 100;

  return (
    <div className="bg-[#232937] rounded-xl overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#10b981] to-[#059669] px-8 py-6">
        <h2 className="text-2xl font-semibold text-[#1a1f2e]">Instructions</h2>
      </div>

      {/* Content Area - Increased to 1800px for less scrolling */}
      <div className="flex h-[1800px]">
        {/* Sidebar Navigation */}
        <div className="w-[280px] bg-[#1a1f2e] border-r border-[#2d3748] overflow-y-auto">
          {tasks.map((task, index) => (
            <div
              key={task.id}
              onClick={() => handleTaskClick(index)}
              className={`flex items-center px-5 py-4 cursor-pointer border-b border-[#2d3748] hover:bg-[#2d3748] transition-all relative ${
                index === currentTaskIndex ? 'bg-[#2d3748] border-l-[3px] border-l-[#10b981] pl-[17px]' : ''
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold mr-3 flex-shrink-0 ${
                completedTasks.has(index) ? 'bg-[#059669]' : 'bg-[#10b981]'
              } text-[#1a1f2e]`}>
                {task.number}
              </div>
              <div className={`text-sm ${index === currentTaskIndex ? 'text-[#e2e8f0] font-medium' : 'text-[#cbd5e1]'}`}>
                {task.title}
              </div>
              {completedTasks.has(index) && (
                <span className="absolute right-5 text-[#10b981] font-bold text-sm">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8 bg-[#1e293b] overflow-y-auto">
          <h3 className="text-[22px] font-semibold text-[#e2e8f0] mb-4">
            Task {currentTask.number}: {currentTask.title}
          </h3>
          <p className="text-[#94a3b8] mb-8 leading-[1.6] text-[15px]">
            {currentTask.description}
          </p>

          {/* Requirements Section */}
          <div className="mb-8">
            <h4 className="text-[#10b981] text-xs uppercase tracking-[1px] mb-4 font-semibold flex items-center">
              <span className="w-5 h-[2px] bg-[#10b981] mr-[10px]"></span>
              What You Need To Do
            </h4>
            
            {currentTask.requirements.map((requirement, reqIndex) => (
              <div key={reqIndex} className="bg-[#232937] border border-[#2d3748] rounded-lg p-4 mb-3 hover:border-[#374151] hover:shadow-md transition-all">
                <div className="text-[#e2e8f0] text-sm leading-[1.8]">
                  <span className="font-medium">{reqIndex + 1}. {requirement.text}</span>
                  {requirement.subitems && (
                    <ul className="mt-2 ml-5 space-y-1">
                      {requirement.subitems.map((item, idx) => (
                        <li key={idx} className="flex items-start">
                          <span className="text-[#10b981] mr-2 mt-0.5">•</span>
                          <span className="text-[#cbd5e1]">{item}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Verification Section */}
          <div className="bg-[#232937] border-2 border-dashed border-[#475569] rounded-lg p-5 mt-6">
            <div className="flex items-center text-[#10b981] font-semibold mb-4 text-base">
              <span className="w-5 h-5 bg-[#10b981] text-[#1a1f2e] rounded-full flex items-center justify-center text-xs mr-[10px]">
                ✓
              </span>
              Verification
            </div>
            
            {currentTask.verification.map((check, checkIndex) => (
              <div
                key={checkIndex}
                onClick={() => toggleVerification(currentTask.id, checkIndex)}
                className="flex items-center p-[10px_12px] bg-[#1a1f2e] rounded-md mb-2 cursor-pointer hover:bg-[#1e293b] hover:translate-x-[3px] transition-all last:mb-0"
              >
                <div className={`w-[18px] h-[18px] border-2 border-[#10b981] rounded flex items-center justify-center mr-3 flex-shrink-0 transition-all ${
                  completedChecks.has(`${currentTask.id}-check-${checkIndex}`) ? 'bg-[#10b981]' : ''
                }`}>
                  {completedChecks.has(`${currentTask.id}-check-${checkIndex}`) && (
                    <span className="text-[#1a1f2e] text-xs font-bold">✓</span>
                  )}
                </div>
                <span className="text-[#cbd5e1] text-sm">{check}</span>
              </div>
            ))}
          </div>

        </div>
      </div>

      {/* Progress Footer */}
      <div className="flex items-center justify-between px-6 py-4 bg-[#1a1f2e] border-t border-[#2d3748]">
        <div className="text-[13px] text-[#94a3b8]">
          Progress: Task {currentTaskIndex + 1} of {tasks.length}
        </div>
        
        <div className="w-[200px] h-[6px] bg-[#2d3748] rounded-[3px] overflow-hidden">
          <div 
            className="h-full bg-gradient-to-r from-[#10b981] to-[#34d399] transition-all duration-500 rounded-[3px]"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <div className="flex gap-[10px]">
          <button
            onClick={handlePreviousTask}
            disabled={currentTaskIndex === 0}
            className="px-4 py-2 bg-[#2d3748] border border-[#374151] text-[#cbd5e1] rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#374151] hover:text-[#e2e8f0] disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <button
            onClick={handleNextTask}
            className="px-4 py-2 bg-[#10b981] border border-[#10b981] text-[#1a1f2e] font-semibold rounded-md text-[13px] cursor-pointer transition-all hover:bg-[#059669] hover:border-[#059669]"
          >
            {currentTaskIndex === tasks.length - 1 ? 'Return to Task 1' : 'Next Task'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DjangoApplicationDisasterInstructions;
